'use client'
import { useEffect, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'

export type ProgressStep = { title: string; sub: string }

/**
 * "Running your diagnostic" staged progress, shared by the interactive tools.
 *
 * Steps check off one at a time on a timer while the real request runs. The
 * parent renders this whenever its `loading` flag is true and swaps in the
 * result when the fetch resolves, so the animation is purely presentational and
 * never blocks or fakes the actual outcome. The last step holds (spinner) until
 * the parent unmounts this.
 */
export function ToolProgress({
  label,
  steps,
  stepMs = 1100,
}: {
  label: string
  steps: ProgressStep[]
  stepMs?: number
}) {
  const [active, setActive] = useState(0)

  useEffect(() => {
    setActive(0)
    const id = setInterval(() => {
      // Advance but hold on the final step until the parent unmounts us.
      setActive((i) => (i < steps.length - 1 ? i + 1 : i))
    }, stepMs)
    return () => clearInterval(id)
  }, [steps.length, stepMs])

  return (
    <div className="mt-8 rounded-2xl border bg-card p-6 sm:p-8" role="status" aria-live="polite">
      <p className="text-center font-display text-xl font-medium tracking-tight">Running your diagnostic</p>

      {/* brand progress bar */}
      <div className="mx-auto mt-4 h-1.5 w-40 overflow-hidden rounded-full bg-muted">
        <div className="h-full w-1/3 animate-[idkslide_1.1s_ease-in-out_infinite] rounded-full bg-gradient-to-r from-primary via-foreground to-primary" />
      </div>
      <style>{`@keyframes idkslide{0%{transform:translateX(-120%)}100%{transform:translateX(420%)}}`}</style>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        Analyzing <span className="font-semibold text-foreground">{label}</span>
      </p>

      <ol className="mx-auto mt-7 grid max-w-sm gap-5">
        {steps.map((s, i) => {
          const done = i < active
          const current = i === active
          return (
            <li key={s.title} className="flex gap-3">
              <span
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                  done
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : current
                      ? 'border-primary text-primary'
                      : 'border-muted text-muted-foreground'
                }`}
              >
                {done ? (
                  <Check className="h-3.5 w-3.5" />
                ) : current ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                )}
              </span>
              <div className={current || done ? '' : 'opacity-50'}>
                <p className={`text-sm font-semibold ${current ? 'text-primary' : 'text-foreground'}`}>{s.title}</p>
                <p className="text-sm text-muted-foreground">{s.sub}</p>
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
